import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Search, Loader2, CalendarClock, XCircle, SearchX, Phone,
  CalendarDays, User, Clock, RotateCcw, Building2, MapPin,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { formatTimeAmPm } from "../lib/timeUtils";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";
import SlotPicker from "../components/SlotPicker";
import StatusBadge from "../components/StatusBadge";
import {
  getMyBooking,
  getSlotAvailability,
  cancelAppointment,
  rescheduleAppointment,
  sendBookingOtp,
  verifyBookingOtp,
} from "../lib/api";
import { normalizeSlotCollection } from "../lib/slotUtils";
import { Reveal } from "../components/Motion";

const today = new Date().toISOString().slice(0, 10);

export default function FindMyBooking() {
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState(today);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [bookingList, setBookingList] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const [rsOpen, setRsOpen] = useState(false);
  const [rsDate, setRsDate] = useState(today);
  const [rsSlots, setRsSlots] = useState([]);
  const [rsLeave, setRsLeave] = useState({ onLeave: false, leaveReason: "" });
  const [rsLoading, setRsLoading] = useState(false);
  const [rsSlot, setRsSlot] = useState("");
  const [rsSubmitting, setRsSubmitting] = useState(false);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const phoneValid = phone.replace(/\D/g, "").length === 10;

  const resetOtpFields = () => {
    setOtpInput("");
    setOtpSent(false);
  };

  const sendOtpForCurrentAction = async (actionLabel) => {
    const targetBooking = selectedBooking ?? booking;
    const bookingPhone = (targetBooking?.phone || "").replace(/\D/g, "");

    if (!bookingPhone) {
      toast.error("Phone number is required for OTP verification.");
      return false;
    }

    setOtpSending(true);
    try {
      const sendRes = await sendBookingOtp(bookingPhone, targetBooking?.clinicId);
      if (!sendRes?.success) {
        toast.error(sendRes?.message || `Could not send OTP for ${actionLabel}.`);
        return false;
      }
      setOtpInput("");
      setOtpSent(true);
      toast.success("OTP sent to your WhatsApp number.");
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.message || `Could not send OTP for ${actionLabel}.`);
      return false;
    } finally {
      setOtpSending(false);
    }
  };

  const search = async () => {
    if (!phoneValid) return;
    setLoading(true); setSearched(true);
    try {
      const result = await getMyBooking(phone, date);
      const list = Array.isArray(result) ? result : result ? [result] : [];
      setBookingList(list);
      setBooking(list[0] ?? null);
      setSelectedBooking(list[0] ?? null);
    }
    catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  const isPastRescheduleDate = (dateString) => {
    if (!dateString) return false;
    const selected = new Date(dateString + "T00:00:00");
    if (Number.isNaN(selected.valueOf())) return false;
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return selected < todayStart;
  };

  const loadRsSlots = async (d, bookingItem = selectedBooking ?? booking) => {
    setRsLoading(true);
    try {
      if (isPastRescheduleDate(d)) {
        setRsSlots([]);
        setRsLeave({ onLeave: false, leaveReason: "" });
        return;
      }
      const doctorId = bookingItem?.doctorId ?? bookingItem?.providerId;
      if (!doctorId) {
        setRsSlots([]);
        setRsLeave({ onLeave: false, leaveReason: "" });
        return;
      }
      const availability = await getSlotAvailability(doctorId, "doctor", d);
      setRsLeave({ onLeave: availability.onLeave, leaveReason: availability.leaveReason });
      let slots = availability.slots;

      // If the selected reschedule date is today, hide slots that have already passed
      try {
        const todayStr = new Date().toISOString().slice(0, 10);
        if (d === todayStr) {
          const now = new Date();
          const nowMins = now.getHours() * 60 + now.getMinutes();
          slots = slots.filter((s) => {
            const timeRaw = (s.time ?? s.slotTime ?? "").toString();
            const hhmm = timeRaw.length >= 5 ? timeRaw.slice(0, 5) : timeRaw;
            const parts = hhmm.split(":");
            if (parts.length < 2) return true;
            const mins = parseInt(parts[0] || "0", 10) * 60 + parseInt(parts[1] || "0", 10);
            return mins > nowMins;
          });

          if (rsSlot) {
            const found = slots.find(s => ((s.time ?? s.slotTime ?? "") + "").slice(0,5) === rsSlot);
            if (!found) setRsSlot("");
          }
        }
      } catch (e) {
        // ignore parsing errors and just show slots
      }

      setRsSlots(() => normalizeSlotCollection(slots));
    } finally { setRsLoading(false); }
  };

  const canReschedule = Boolean(booking) && (booking?.rescheduleCount == null || booking?.rescheduleCount < 1);
  const alreadyRescheduled = Boolean(booking) && booking?.rescheduleCount >= 1;

  useEffect(() => {
    if (!rsOpen || !booking || !canReschedule) return;

    const intervalId = window.setInterval(() => {
      loadRsSlots(rsDate);
    }, 20000);

    return () => window.clearInterval(intervalId);
  }, [rsOpen, rsDate, booking, canReschedule]);

  const openReschedule = async (bookingItem = selectedBooking ?? booking) => {
    if (!bookingItem || (bookingItem?.rescheduleCount != null && bookingItem.rescheduleCount >= 1)) {
      return;
    }
    resetOtpFields();
    setSelectedBooking(bookingItem);
    setRsOpen(true);
    setRsSlot("");
    const slotDate = bookingItem?.date ?? bookingItem?.appointmentDate ?? rsDate;
    setRsDate(slotDate);
    await loadRsSlots(slotDate, bookingItem);
  };
  const changeRsDate = async (d) => {
    if (isPastRescheduleDate(d)) {
      toast.error("Cannot choose a past date for rescheduling.");
      setRsSlots([]);
      setRsLeave({ onLeave: false, leaveReason: "" });
      setRsDate(d);
      setRsSlot("");
      return;
    }

    setRsDate(d);
    setRsSlot("");
    await loadRsSlots(d);
  };

  const doReschedule = async () => {
    const targetBooking = selectedBooking ?? booking;
    if (isPastRescheduleDate(rsDate)) {
      toast.error("Cannot reschedule to a past date.");
      return;
    }
    setRsSubmitting(true);
    try {
      const appointmentId = targetBooking?.appointmentId ?? targetBooking?.id;
      if (!appointmentId) {
        toast.error("Cannot reschedule without a valid appointment.");
        return;
      }

      const phone = (targetBooking?.phone || "").replace(/\D/g, "");
      if (!phone) {
        toast.error("Phone number is required for OTP verification.");
        return;
      }

      if (!otpInput || otpInput.trim().length < 6) {
        toast.error("Enter the 6-digit OTP sent to your WhatsApp number.");
        return;
      }

      const verifyRes = await verifyBookingOtp(phone, otpInput.trim());
      if (!verifyRes?.success) {
        toast.error(verifyRes?.message || "Invalid OTP. Please try again.");
        return;
      }

      await rescheduleAppointment(appointmentId, rsDate, rsSlot, otpInput.trim());
      const refreshed = bookingList.map((item) =>
        (item?.appointmentId ?? item?.id) === appointmentId
          ? { ...item, date: rsDate, slot: rsSlot, status: "rescheduled", rescheduleCount: (item?.rescheduleCount ?? 0) + 1 }
          : item
      );
      setBookingList(refreshed);
      setBooking(refreshed[0] ?? null);
      setSelectedBooking(refreshed.find((item) => (item?.appointmentId ?? item?.id) === appointmentId) ?? null);
      resetOtpFields();
      setRsOpen(false);
      toast.success("Appointment rescheduled.");
    } catch (error) {
      const message = error?.response?.data?.message ||
        (error?.response?.status === 403 && "You can reschedule only once.") ||
        "Could not reschedule. Please try again.";
      toast.error(message);
    } finally { setRsSubmitting(false); }
  };

  const doCancel = async () => {
    const targetBooking = selectedBooking ?? booking;
    setCancelling(true);
    try {
      const appointmentId = targetBooking?.appointmentId ?? targetBooking?.id;
      if (!appointmentId) {
        toast.error("Cannot cancel without a valid appointment.");
        return;
      }

      const phone = (targetBooking?.phone || "").replace(/\D/g, "");
      if (!phone) {
        toast.error("Phone number is required for OTP verification.");
        return;
      }

      if (!otpInput || otpInput.trim().length < 6) {
        toast.error("Enter the 6-digit OTP sent to your WhatsApp number.");
        return;
      }

      const verifyRes = await verifyBookingOtp(phone, otpInput.trim());
      if (!verifyRes?.success) {
        toast.error(verifyRes?.message || "Invalid OTP. Please try again.");
        return;
      }

      await cancelAppointment(appointmentId, otpInput.trim());
      const refreshed = bookingList.map((item) =>
        (item?.appointmentId ?? item?.id) === appointmentId ? { ...item, status: "cancelled" } : item
      );
      setBookingList(refreshed);
      setBooking(refreshed[0] ?? null);
      setSelectedBooking(refreshed.find((item) => (item?.appointmentId ?? item?.id) === appointmentId) ?? null);
      resetOtpFields();
      setCancelOpen(false); toast.success("Appointment cancelled.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not cancel.");
    }
    finally { setCancelling(false); }
  };

  const active = booking && booking.status?.toString().toLowerCase() !== "cancelled";

  return (
    <div className="relative min-h-screen pt-28 pb-24">
      <div className="glow-teal pointer-events-none absolute top-24 left-0 h-[380px] w-[520px] blur-[120px] opacity-40" />
      <div className="relative mx-auto max-w-4xl px-5 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">Find my booking</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-medium text-foreground leading-tight">Look up and manage your visit.</h1>
          <p className="mt-3 text-muted-foreground text-lg">Search with your mobile number and appointment date.</p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-8 glass rounded-3xl p-6 sm:p-8">
            <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-4 items-end">
              <div>
                <Label className="text-muted-foreground">Mobile number</Label>
                <div className="mt-2 relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
                  <div className="relative">
                    <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">+91</span>
                    <Input data-testid="find-phone" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      placeholder="Enter your 10-digit mobile number" inputMode="numeric" maxLength={10}
                      className="pl-14 h-12 rounded-xl bg-muted/50 border-border text-foreground placeholder:text-muted-foreground/70 focus-visible:ring-ring/50" />
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Date</Label>
                <Input data-testid="find-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="mt-2 h-12 rounded-xl bg-muted/50 border-border text-foreground dark:[color-scheme:dark] focus-visible:ring-ring/50" />
              </div>
              <button data-testid="find-search-btn" onClick={search} disabled={!phoneValid || loading}
                className="aurora-gradient text-white font-semibold h-12 px-7 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-40 hover:scale-[1.02] transition-transform">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
              </button>
            </div>
          </div>
        </Reveal>

        <div className="mt-8">
          {loading && <div className="glass rounded-3xl p-8 animate-pulse space-y-4"><div className="h-4 w-40 bg-muted rounded" /><div className="h-8 w-64 bg-muted rounded" /></div>}

          {!loading && searched && bookingList.length === 0 && (
            <div data-testid="find-empty" className="glass rounded-3xl p-12 text-center border-dashed">
              <div className="mx-auto h-14 w-14 rounded-full bg-muted grid place-items-center text-muted-foreground/70"><SearchX className="h-7 w-7" /></div>
              <h3 className="mt-5 font-display text-2xl text-foreground">We couldn&apos;t find a booking for this search</h3>
              <p className="mt-2 text-muted-foreground">Please check the mobile number and date, then try again.</p>
            </div>
          )}

          {!loading && bookingList.length > 0 && (
            <div className="space-y-6">
              {bookingList.map((bookingItem, index) => {
                const itemActive = bookingItem && bookingItem.status?.toString().toLowerCase() !== "cancelled";
                const itemCanReschedule = Boolean(bookingItem) && (bookingItem?.rescheduleCount == null || bookingItem?.rescheduleCount < 1);
                const itemAlreadyRescheduled = Boolean(bookingItem) && bookingItem?.rescheduleCount >= 1;

                return (
                  <motion.div key={bookingItem?.appointmentId ?? bookingItem?.id ?? index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                    data-testid="booking-result" className="glass rounded-3xl p-8">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground/70">Appointment {bookingItem?.appointmentId ?? bookingItem?.id}</p>
                        <h3 className="mt-2 font-display text-3xl text-foreground">Token #{bookingItem?.token ?? bookingItem?.tokenNo ?? "—"}</h3>
                      </div>
                      <StatusBadge status={bookingItem?.status} />
                    </div>

                    <div className="mt-8 grid sm:grid-cols-2 gap-6">
                      <Info icon={User} label="Patient" value={bookingItem?.patientName} />
                      <Info icon={Phone} label="Mobile" value={`+91 ${bookingItem?.phone}`} />
                      <Info icon={Building2} label={bookingItem?.clinicName ? "Clinic" : "Doctor"} value={bookingItem?.clinicName || bookingItem?.doctorName} />
                      {(bookingItem?.city || bookingItem?.state) && <Info icon={MapPin} label="Location" value={[bookingItem?.city, bookingItem?.state].filter(Boolean).join(", ")} />}
                      <Info icon={CalendarDays} label="Date" value={bookingItem?.date ?? bookingItem?.appointmentDate} />
                      <Info icon={Clock} label="Time" value={formatTimeAmPm(bookingItem?.slot ?? (bookingItem?.slotTime ? bookingItem.slotTime.slice(0, 5) : ""))} />
                    </div>

                    {itemActive ? (
                      <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <button data-testid="open-reschedule-btn" onClick={() => openReschedule(bookingItem)} disabled={!itemCanReschedule}
                          className={`inline-flex items-center justify-center gap-2 glass-soft text-foreground font-semibold px-6 py-3 rounded-full transition-colors ${!itemCanReschedule ? "opacity-50 cursor-not-allowed border-border" : "hover:border-primary"}`}>
                          <CalendarClock className="h-4 w-4" /> Reschedule
                        </button>
                        <button data-testid="open-cancel-btn" onClick={() => { setSelectedBooking(bookingItem); setCancelOpen(true); }}
                          className="inline-flex items-center justify-center gap-2 bg-rose-500/10 border border-rose-500/30 text-rose-300 font-semibold px-6 py-3 rounded-full hover:bg-rose-500/20 transition-colors">
                          <XCircle className="h-4 w-4" /> Cancel appointment
                        </button>
                      </div>
                    ) : (
                      <div className="mt-8 rounded-2xl bg-rose-500/10 border border-rose-500/30 p-5 text-rose-300 flex items-center gap-3">
                        <XCircle className="h-5 w-5" /> This appointment has been cancelled.
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Reschedule dialog */}
      <Dialog open={rsOpen} onOpenChange={setRsOpen}>
        <DialogContent className="sm:max-w-lg rounded-3xl bg-card border-border text-foreground overflow-hidden" data-testid="reschedule-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2 text-foreground"><CalendarClock className="h-5 w-5 text-primary" /> Reschedule appointment</DialogTitle>
          </DialogHeader>
          <div className="mt-2 max-h-[calc(100vh-160px)] overflow-y-auto pr-2">
            {alreadyRescheduled && (
              <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-amber-900 mb-4">
                This appointment can only be rescheduled once. You have already used your one reschedule.
              </div>
            )}
            <Label className="text-muted-foreground">New date</Label>
            <Input type="date" min={today} value={rsDate} onChange={(e) => changeRsDate(e.target.value)} data-testid="reschedule-date"
              className="mt-2 h-11 rounded-xl bg-muted border-border text-foreground dark:[color-scheme:dark] max-w-xs" />
            <div className="mt-5">
              <div className="flex items-center justify-between gap-3">
                <Label className="text-muted-foreground">New slot</Label>
              </div>
              <div className="mt-2"><SlotPicker slots={rsSlots} value={rsSlot} onChange={setRsSlot} loading={rsLoading} onLeave={rsLeave.onLeave} leaveReason={rsLeave.leaveReason} /></div>
            </div>
            <div className="mt-5 rounded-2xl border border-border bg-muted/30 p-4">
              {!otpSent ? (
                <div className="flex justify-end">
                  <button type="button" onClick={() => sendOtpForCurrentAction("reschedule")} disabled={otpSending}
                    className="text-sm font-medium text-primary disabled:opacity-50">
                    {otpSending ? "Sending..." : "Send OTP"}
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <Label className="text-muted-foreground">OTP verification</Label>
                    <button type="button" onClick={() => sendOtpForCurrentAction("reschedule")} disabled={otpSending}
                      className="text-sm font-medium text-primary disabled:opacity-50">
                      {otpSending ? "Sending..." : "Resend OTP"}
                    </button>
                  </div>
                  <Input value={otpInput} onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="Enter 6-digit OTP" inputMode="numeric" maxLength={6}
                    className="h-11 rounded-xl bg-background border-border text-foreground placeholder:text-muted-foreground/70" />
                </>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <button onClick={() => { resetOtpFields(); setRsOpen(false); }} className="px-5 py-2.5 rounded-full text-muted-foreground font-medium hover:bg-foreground/5 transition-colors">Keep current</button>
            <button data-testid="confirm-reschedule-btn" onClick={doReschedule} disabled={!rsSlot || rsSubmitting || !canReschedule || otpInput.trim().length < 6}
              className="aurora-gradient text-white font-semibold px-6 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-40 hover:scale-[1.02] transition-transform">
              {rsSubmitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <><RotateCcw className="h-4 w-4" /> Confirm new time</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel alert */}
      <AlertDialog open={cancelOpen} onOpenChange={(open) => {
        setCancelOpen(open);
        if (!open) resetOtpFields();
      }}>
        <AlertDialogContent className="rounded-3xl bg-card border-border text-foreground" data-testid="cancel-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-foreground">Cancel this appointment?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">This will release your slot and cannot be undone. You can always book again afterwards.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-4 rounded-2xl border border-border bg-muted/30 p-4">
            {!otpSent ? (
              <div className="flex justify-end">
                <button type="button" onClick={() => sendOtpForCurrentAction("cancellation")} disabled={otpSending}
                  className="text-sm font-medium text-primary disabled:opacity-50">
                  {otpSending ? "Sending..." : "Send OTP"}
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <Label className="text-muted-foreground">OTP verification</Label>
                  <button type="button" onClick={() => sendOtpForCurrentAction("cancellation")} disabled={otpSending}
                    className="text-sm font-medium text-primary disabled:opacity-50">
                    {otpSending ? "Sending..." : "Resend OTP"}
                  </button>
                </div>
                <Input value={otpInput} onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="Enter 6-digit OTP" inputMode="numeric" maxLength={6}
                  className="h-11 rounded-xl bg-background border-border text-foreground placeholder:text-muted-foreground/70" />
              </>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={resetOtpFields} className="rounded-full bg-muted border-border text-foreground hover:bg-muted">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-cancel-btn" onClick={(e) => { e.preventDefault(); doCancel(); }}
              disabled={otpInput.trim().length < 6 || cancelling} className="rounded-full bg-rose-600 hover:bg-rose-700 disabled:opacity-40">
              {cancelling ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Cancelling…</> : "Yes, cancel"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 rounded-xl bg-muted/60 border border-border grid place-items-center text-primary shrink-0"><Icon className="h-5 w-5" /></div>
      <div>
        <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground/70">{label}</p>
        <p className="mt-0.5 font-medium text-foreground">{value}</p>
      </div>
    </div>
  );
}
